import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Switch,
  FormControlLabel,
  IconButton,
  Tooltip,
  Avatar,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  QrCode as QrCodeIcon,
  AccountBalance as BankIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
  CloudUpload as CloudUploadIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import { merchantUPIAPI } from '../../../services/api';
import { toast } from 'react-toastify';

const PAYMENT_TYPES = {
  NORMAL_UPI: 'normal_upi',
  MERCHANT_UPI: 'merchant_upi',
  BANK_DETAILS: 'bank_details',
};

const PAYMENT_TYPE_LABELS = {
  [PAYMENT_TYPES.NORMAL_UPI]: 'Normal UPI',
  [PAYMENT_TYPES.MERCHANT_UPI]: 'Merchant UPI',
  [PAYMENT_TYPES.BANK_DETAILS]: 'Bank Details',
};

const getEffectivePaymentType = (upi) => upi?.payment_type || PAYMENT_TYPES.NORMAL_UPI;

const MerchantUPIManagement = () => {
  const [merchantUPIs, setMerchantUPIs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUPI, setEditingUPI] = useState(null);
  const [qrCodeFile, setQrCodeFile] = useState(null);
  const [qrCodePreview, setQrCodePreview] = useState(null);
  
  const [formData, setFormData] = useState({
    payment_type: PAYMENT_TYPES.NORMAL_UPI,
    upi_id: '',
    merchant_name: '',
    merchant_code: '',
    bank_account_holder_name: '',
    bank_account_number: '',
    bank_name: '',
    ifsc_code: '',
    bank_branch_name: '',
    is_active: true,
    is_primary: false,
    daily_limit: 100000,
    monthly_limit: 3000000,
    description: '',
  });

  useEffect(() => {
    fetchMerchantUPIs();
  }, []);

  const fetchMerchantUPIs = async () => {
    try {
      setLoading(true);
      const response = await merchantUPIAPI.getMerchantUPIs({ include_inactive: true });
      if (response.data.success) {
        setMerchantUPIs(response.data.data);
      }
    } catch (error) {
      toast.error('Failed to fetch merchant UPIs');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUPI = () => {
    setEditingUPI(null);
    setQrCodeFile(null);
    setQrCodePreview(null);
    setFormData({
      payment_type: PAYMENT_TYPES.NORMAL_UPI,
      upi_id: '',
      merchant_name: '',
      merchant_code: '',
      bank_account_holder_name: '',
      bank_account_number: '',
      bank_name: '',
      ifsc_code: '',
      bank_branch_name: '',
      is_active: true,
      is_primary: false,
      daily_limit: 100000,
      monthly_limit: 3000000,
      description: '',
    });
    setOpenDialog(true);
  };

  const handleEditUPI = (upi) => {
    const paymentType = getEffectivePaymentType(upi);
    setEditingUPI(upi);
    setQrCodeFile(null);
    setQrCodePreview(paymentType === PAYMENT_TYPES.BANK_DETAILS ? null : (upi.qr_code_url || null));
    setFormData({
      payment_type: paymentType,
      upi_id: upi.upi_id || '',
      merchant_name: upi.merchant_name || '',
      merchant_code: upi.merchant_code || '',
      bank_account_holder_name: upi.bank_account_holder_name || '',
      bank_account_number: upi.bank_account_number || '',
      bank_name: upi.bank_name || '',
      ifsc_code: upi.ifsc_code || '',
      bank_branch_name: upi.bank_branch_name || '',
      is_active: upi.is_active,
      is_primary: upi.is_primary,
      daily_limit: upi.daily_limit,
      monthly_limit: upi.monthly_limit,
      description: upi.description || '',
    });
    setOpenDialog(true);
  };

  const parseNumberInput = (value) => {
    if (value === '') {
      return '';
    }

    const parsed = Number(value);
    return Number.isNaN(parsed) ? '' : parsed;
  };

  const handlePaymentTypeChange = (paymentType) => {
    setFormData((prev) => ({
      ...prev,
      payment_type: paymentType,
      ...(paymentType === PAYMENT_TYPES.BANK_DETAILS
        ? {
            upi_id: '',
            merchant_name: '',
            merchant_code: '',
          }
        : {
            bank_account_holder_name: '',
            bank_account_number: '',
            bank_name: '',
            ifsc_code: '',
            bank_branch_name: '',
          }),
    }));

    if (paymentType === PAYMENT_TYPES.BANK_DETAILS) {
      setQrCodeFile(null);
      setQrCodePreview(null);
    }
  };

  const isBankDetailsType = formData.payment_type === PAYMENT_TYPES.BANK_DETAILS;
  const isMerchantUPIType = formData.payment_type === PAYMENT_TYPES.MERCHANT_UPI;

  const isSubmitDisabled = () => {
    if (loading) {
      return true;
    }

    if (isBankDetailsType) {
      return !formData.bank_account_holder_name || !formData.bank_account_number || !formData.bank_name || !formData.ifsc_code;
    }

    if (!formData.upi_id) {
      return true;
    }

    if (isMerchantUPIType && !formData.merchant_name) {
      return true;
    }

    return false;
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      
      // Create FormData for file upload support
      const submitFormData = new FormData();
      const appendIfPresent = (key, value) => {
        if (value !== undefined && value !== null && value !== '') {
          submitFormData.append(key, value);
        }
      };

      submitFormData.append('payment_type', formData.payment_type);

      if (!isBankDetailsType) {
        appendIfPresent('upi_id', formData.upi_id?.trim());
        if (isMerchantUPIType) {
          appendIfPresent('merchant_name', formData.merchant_name?.trim());
          appendIfPresent('merchant_code', formData.merchant_code?.trim());
        }
      } else {
        appendIfPresent('bank_account_holder_name', formData.bank_account_holder_name?.trim());
        appendIfPresent('bank_account_number', formData.bank_account_number?.trim());
        appendIfPresent('bank_name', formData.bank_name?.trim());
        appendIfPresent('ifsc_code', formData.ifsc_code?.trim().toUpperCase());
        appendIfPresent('bank_branch_name', formData.bank_branch_name?.trim());
      }

      submitFormData.append('is_active', formData.is_active);
      submitFormData.append('is_primary', formData.is_primary);
      if (formData.daily_limit !== '' && formData.daily_limit !== null) {
        submitFormData.append('daily_limit', formData.daily_limit);
      }
      if (formData.monthly_limit !== '' && formData.monthly_limit !== null) {
        submitFormData.append('monthly_limit', formData.monthly_limit);
      }
      appendIfPresent('description', formData.description?.trim());
      
      // Append QR code file if selected
      if (qrCodeFile && !isBankDetailsType) {
        submitFormData.append('qr_code', qrCodeFile);
      }

      if (editingUPI) {
        const response = await merchantUPIAPI.updateMerchantUPI(editingUPI.id, submitFormData);
        if (response.data.success) {
          toast.success('Merchant UPI updated successfully');
        }
      } else {
        const response = await merchantUPIAPI.createMerchantUPI(submitFormData);
        if (response.data.success) {
          toast.success('Merchant UPI created successfully');
        }
      }
      
      setOpenDialog(false);
      setQrCodeFile(null);
      setQrCodePreview(null);
      fetchMerchantUPIs();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleQrCodeFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Only image files (jpeg, jpg, png, gif, webp) are allowed!');
        return;
      }
      
      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      
      setQrCodeFile(file);
      
      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setQrCodePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveQrCode = () => {
    if (!qrCodeFile) {
      return;
    }

    setQrCodeFile(null);
    if (editingUPI?.qr_code_url) {
      setQrCodePreview(editingUPI.qr_code_url);
    } else {
      setQrCodePreview(null);
    }
  };

  const getTypeChipColor = (paymentType) => {
    if (paymentType === PAYMENT_TYPES.BANK_DETAILS) {
      return 'info';
    }
    if (paymentType === PAYMENT_TYPES.MERCHANT_UPI) {
      return 'secondary';
    }
    return 'default';
  };

  const handleDeleteUPI = async (id) => {
    if (window.confirm('Are you sure you want to delete this UPI?')) {
      try {
        setLoading(true);
        const response = await merchantUPIAPI.deleteMerchantUPI(id);
        if (response.data.success) {
          toast.success('Merchant UPI deleted successfully');
          fetchMerchantUPIs();
        }
      } catch (error) {
        toast.error(error.response?.data?.message || 'Delete failed');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      setLoading(true);
      const response = await merchantUPIAPI.toggleUPIStatus(id);
      if (response.data.success) {
        toast.success(response.data.message);
        fetchMerchantUPIs();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Toggle failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSetPrimary = async (id) => {
    try {
      setLoading(true);
      const response = await merchantUPIAPI.setPrimaryUPI(id);
      if (response.data.success) {
        toast.success('Primary UPI set successfully');
        fetchMerchantUPIs();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Set primary failed');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          Merchant UPI Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateUPI}
          disabled={loading}
        >
          Add UPI
        </Button>
      </Box>

      <Card>
        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Type & Details</TableCell>
                  <TableCell>Merchant / Bank Info</TableCell>
                  <TableCell>Limits</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>QR Code</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {merchantUPIs.map((upi) => (
                  (() => {
                    const paymentType = getEffectivePaymentType(upi);
                    const isBankRow = paymentType === PAYMENT_TYPES.BANK_DETAILS;

                    return (
                  <TableRow key={upi.id}>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                          <BankIcon />
                        </Avatar>
                        <Box>
                          <Chip
                            label={PAYMENT_TYPE_LABELS[paymentType] || paymentType}
                            color={getTypeChipColor(paymentType)}
                            size="small"
                            sx={{ mb: 0.5 }}
                          />
                          <Typography variant="subtitle2" fontWeight="bold">
                            {isBankRow ? (upi.bank_account_holder_name || 'Bank Transfer') : (upi.upi_id || '--')}
                          </Typography>
                          {upi.merchant_code && (
                            <Typography variant="caption" color="text.secondary">
                              Code: {upi.merchant_code}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {isBankRow ? (
                        <>
                          <Typography variant="subtitle2">{upi.bank_name || '--'}</Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            A/C: {upi.bank_account_number || '--'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            IFSC: {upi.ifsc_code || '--'}
                          </Typography>
                          {upi.bank_branch_name && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              Branch: {upi.bank_branch_name}
                            </Typography>
                          )}
                        </>
                      ) : (
                        <Typography variant="subtitle2">{upi.merchant_name || '--'}</Typography>
                      )}
                      {upi.description && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {upi.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" display="block">
                        Daily: {formatCurrency(upi.daily_limit)}
                      </Typography>
                      <Typography variant="caption" display="block">
                        Monthly: {formatCurrency(upi.monthly_limit)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" flexDirection="column" gap={1}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Chip 
                            label={upi.is_active ? 'Active' : 'Inactive'} 
                            color={upi.is_active ? 'success' : 'default'}
                            size="small"
                          />
                          {upi.is_primary && (
                            <Chip 
                              icon={<StarIcon />}
                              label="Primary" 
                              color="warning"
                              size="small"
                            />
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {!isBankRow && upi.qr_code_url ? (
                        <Tooltip title="QR Code Available">
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={() => window.open(upi.qr_code_url, '_blank')}
                          >
                            <QrCodeIcon />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          {isBankRow ? 'Not Applicable' : 'No QR'}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <Tooltip title="Edit">
                          <IconButton 
                            size="small" 
                            onClick={() => handleEditUPI(upi)}
                            disabled={loading}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        
                        <Tooltip title={upi.is_active ? 'Deactivate' : 'Activate'}>
                          <IconButton 
                            size="small" 
                            color={upi.is_active ? 'warning' : 'success'}
                            onClick={() => handleToggleStatus(upi.id)}
                            disabled={loading}
                          >
                            {upi.is_active ? <ToggleOffIcon /> : <ToggleOnIcon />}
                          </IconButton>
                        </Tooltip>
                        
                        {!upi.is_primary && upi.is_active && (
                          <Tooltip title="Set as Primary">
                            <IconButton 
                              size="small" 
                              color="warning"
                              onClick={() => handleSetPrimary(upi.id)}
                              disabled={loading}
                            >
                              <StarBorderIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        
                        <Tooltip title="Delete">
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={() => handleDeleteUPI(upi.id)}
                            disabled={loading}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                    );
                  })()
                ))}
                {merchantUPIs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography color="text.secondary">No merchant UPIs found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingUPI ? 'Edit Merchant UPI' : 'Add Merchant UPI'}
          <IconButton
            aria-label="close"
            onClick={() => setOpenDialog(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              select
              label="Payment Type"
              fullWidth
              value={formData.payment_type}
              onChange={(e) => handlePaymentTypeChange(e.target.value)}
            >
              <MenuItem value={PAYMENT_TYPES.NORMAL_UPI}>Normal UPI</MenuItem>
              <MenuItem value={PAYMENT_TYPES.MERCHANT_UPI}>Merchant UPI</MenuItem>
              <MenuItem value={PAYMENT_TYPES.BANK_DETAILS}>Bank Details</MenuItem>
            </TextField>

            {!isBankDetailsType && (
              <TextField
                label="UPI ID"
                fullWidth
                required
                value={formData.upi_id}
                onChange={(e) => setFormData({ ...formData, upi_id: e.target.value })}
                placeholder="merchant@paytm"
              />
            )}

            {isMerchantUPIType && !isBankDetailsType && (
              <Box display="flex" gap={2}>
                <TextField
                  label="Merchant Name"
                  fullWidth
                  required
                  value={formData.merchant_name}
                  onChange={(e) => setFormData({ ...formData, merchant_name: e.target.value })}
                />

                <TextField
                  label="Merchant Code"
                  fullWidth
                  value={formData.merchant_code}
                  onChange={(e) => setFormData({ ...formData, merchant_code: e.target.value })}
                  placeholder="Optional"
                />
              </Box>
            )}

            {isBankDetailsType && (
              <>
                <TextField
                  label="Account Holder Name"
                  fullWidth
                  required
                  value={formData.bank_account_holder_name}
                  onChange={(e) => setFormData({ ...formData, bank_account_holder_name: e.target.value })}
                />

                <Box display="flex" gap={2}>
                  <TextField
                    label="Account Number"
                    fullWidth
                    required
                    value={formData.bank_account_number}
                    onChange={(e) => setFormData({ ...formData, bank_account_number: e.target.value })}
                  />

                  <TextField
                    label="IFSC Code"
                    fullWidth
                    required
                    value={formData.ifsc_code}
                    onChange={(e) => setFormData({ ...formData, ifsc_code: e.target.value.toUpperCase() })}
                  />
                </Box>

                <Box display="flex" gap={2}>
                  <TextField
                    label="Bank Name"
                    fullWidth
                    required
                    value={formData.bank_name}
                    onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                  />

                  <TextField
                    label="Branch Name"
                    fullWidth
                    value={formData.bank_branch_name}
                    onChange={(e) => setFormData({ ...formData, bank_branch_name: e.target.value })}
                    placeholder="Optional"
                  />
                </Box>
              </>
            )}

            {!isBankDetailsType && !isMerchantUPIType && (
              <TextField
                label="Display Name"
                fullWidth
                value={formData.merchant_name}
                onChange={(e) => setFormData({ ...formData, merchant_name: e.target.value })}
                placeholder="Optional"
              />
            )}
            
            {/* QR Code File Upload */}
            {!isBankDetailsType && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                QR Code Image
              </Typography>
              <Box
                sx={{
                  border: '2px dashed',
                  borderColor: 'divider',
                  borderRadius: 2,
                  p: 2,
                  textAlign: 'center',
                  bgcolor: 'background.default',
                }}
              >
                {qrCodePreview ? (
                  <Box>
                    <Box
                      component="img"
                      src={qrCodePreview}
                      alt="QR Code Preview"
                      sx={{
                        maxWidth: 200,
                        maxHeight: 200,
                        objectFit: 'contain',
                        mb: 1,
                        borderRadius: 1,
                      }}
                    />
                    <Box display="flex" justifyContent="center" gap={1}>
                      <Button
                        variant="outlined"
                        component="label"
                        size="small"
                        startIcon={<CloudUploadIcon />}
                      >
                        Change
                        <input
                          type="file"
                          hidden
                          accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                          onChange={handleQrCodeFileChange}
                        />
                      </Button>
                      {qrCodeFile && (
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          startIcon={<DeleteIcon />}
                          onClick={handleRemoveQrCode}
                        >
                          Remove
                        </Button>
                      )}
                    </Box>
                  </Box>
                ) : (
                  <Box>
                    <ImageIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Upload QR Code Image (Max 5MB)
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                      Supported formats: JPEG, JPG, PNG, GIF, WEBP
                    </Typography>
                    <Button
                      variant="contained"
                      component="label"
                      startIcon={<CloudUploadIcon />}
                    >
                      Upload QR Code
                      <input
                        type="file"
                        hidden
                        accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                        onChange={handleQrCodeFileChange}
                      />
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>
            )}
            
            <Box display="flex" gap={2}>
              <TextField
                label="Daily Limit"
                type="number"
                fullWidth
                value={formData.daily_limit}
                onChange={(e) => setFormData({ ...formData, daily_limit: parseNumberInput(e.target.value) })}
                inputProps={{ min: 0, step: 1000 }}
              />
              
              <TextField
                label="Monthly Limit"
                type="number"
                fullWidth
                value={formData.monthly_limit}
                onChange={(e) => setFormData({ ...formData, monthly_limit: parseNumberInput(e.target.value) })}
                inputProps={{ min: 0, step: 10000 }}
              />
            </Box>
            
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={2}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Optional description"
            />
            
            <Box display="flex" gap={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                }
                label="Active"
              />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_primary}
                    onChange={(e) => setFormData({ ...formData, is_primary: e.target.checked })}
                  />
                }
                label="Set as Primary"
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleSubmit}
            disabled={isSubmitDisabled()}
          >
            {editingUPI ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MerchantUPIManagement;